import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  coverageLabel,
  describeLive,
  describeReadiness,
  describeStatus,
  summariseMissingLocales,
} from "@/lib/blog/list-view";
import { LTR_ISOLATE_ATTRS } from "@/lib/blog/locale-options";
import type { BlogClusterRow } from "@/lib/blog/types";

interface BlogPostCardsProps {
  rows: BlogClusterRow[];
  onSelect: (row: BlogClusterRow) => void;
}

/**
 * The narrow-viewport list (AM-15.1).
 *
 * `VirtualAdminTable` fixes `gridTemplateColumns` and absolutely positions its
 * rows at a measured height, so no amount of CSS makes it stack. Below 720px
 * the page renders this instead: the same fields, one card per post, with the
 * column names carried as inline labels because there is no header row left to
 * refer back to.
 */
export function BlogPostCards({ rows, onSelect }: BlogPostCardsProps) {
  return (
    <ul className="flex flex-col gap-3" data-testid="blog-post-cards">
      {rows.map((row) => {
        const status = describeStatus(row.status);
        const readiness = describeReadiness(row.readiness);
        const live = describeLive(row);
        const missing = summariseMissingLocales(row.missingLocales);

        return (
          <li key={row.slug}>
            <button
              type="button"
              onClick={() => {
                onSelect(row);
              }}
              className={cn(
                "w-full rounded-2xl border p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                row.status === "archived" && "opacity-70",
              )}
            >
              <span className="flex flex-wrap items-center gap-2">
                <Badge variant={status.variant}>{status.label}</Badge>
                <Badge variant={readiness.variant}>{readiness.label}</Badge>
              </span>
              <span className="mt-2 block font-medium">{row.title}</span>
              <span
                className="mt-1 block font-mono text-xs text-muted-foreground"
                {...LTR_ISOLATE_ATTRS}
              >
                {row.slug}
              </span>

              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div>
                  <dt className="text-muted-foreground">Locales</dt>
                  <dd className="tabular-nums">{coverageLabel(row)}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Live</dt>
                  <dd>{live.live}</dd>
                </div>
                {missing.all.length > 0 && (
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Missing</dt>
                    <dd className="font-mono" {...LTR_ISOLATE_ATTRS}>
                      {missing.inline.join(" ")}
                      {missing.overflow > 0 && (
                        <span className="ml-1 font-sans text-muted-foreground">
                          {missing.overflowLabel}
                        </span>
                      )}
                    </dd>
                  </div>
                )}
                {live.hasDrift && (
                  <div className="col-span-2">
                    <dt className="text-muted-foreground">Waiting to ship</dt>
                    <dd>{live.drift}</dd>
                  </div>
                )}
              </dl>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
